import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdClose as MdCloseRaw,
  MdVisibility as MdVisibilityRaw,
  MdCheckCircle as MdCheckCircleRaw,
  MdCancel as MdCancelRaw,
  MdHourglassEmpty as MdHourglassEmptyRaw,
  MdReportProblem as MdReportProblemRaw,
  MdAdd as MdAddRaw,
} from 'react-icons/md';
import { authenticatedFetch, resolveFileUrl } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';
import { FileDownloadLink } from '../../components/shared/FileDownloadLink';
import { ClearFiltersButton, PageHeader, TableBodySkeleton } from '@euroasiann/shared/ui';
import { RaiseClaimModal } from './RaiseClaimModal';

type IconComp = ComponentType<{ className?: string }>;
const MdClose = MdCloseRaw as IconComp;
const MdVisibility = MdVisibilityRaw as IconComp;
const MdCheckCircle = MdCheckCircleRaw as IconComp;
const MdCancel = MdCancelRaw as IconComp;
const MdHourglassEmpty = MdHourglassEmptyRaw as IconComp;
const MdReportProblem = MdReportProblemRaw as IconComp;
const MdAdd = MdAddRaw as IconComp;

type ClaimStatus = 'pending' | 'accepted' | 'rejected';

interface ClaimRecord {
  _id: string;
  claimNumber: string;
  status: ClaimStatus;
  poNumber: string;
  rfqNumber?: string;
  customerName?: string;
  vesselName?: string;
  category?: string;
  brand?: string;
  equipmentModel?: string;
  reason: string;
  rejectionReason?: string;
  items: Array<{ itemDescription: string; partNo?: string; quantity: number; uom: string; unitPrice?: number; total?: number }>;
  documents?: Array<{ fileName: string; fileUrl: string }>;
  createdAt: string;
}

const statusConfig: Record<ClaimStatus, { label: string; className: string; icon: ReactNode }> = {
  pending: {
    label: 'Pending',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <MdHourglassEmpty className="w-3.5 h-3.5" />,
  },
  accepted: {
    label: 'Accepted',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <MdCheckCircle className="w-3.5 h-3.5" />,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: <MdCancel className="w-3.5 h-3.5" />,
  },
};

function formatDate(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function ClaimDetailModal({ claim, onClose }: { claim: ClaimRecord; onClose: () => void }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const status = statusConfig[claim.status];

  const respondMutation = useMutation({
    mutationFn: async (action: 'accept' | 'reject') => {
      const res = await authenticatedFetch(`/api/v1/admin/claims/${claim._id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          rejectionReason: action === 'reject' ? rejectionReason.trim() : undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to respond to claim');
      }
      return res.json();
    },
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ['admin-delivery-claims'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
      showToast(action === 'accept' ? 'Claim accepted' : 'Claim rejected', 'success');
      onClose();
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-[#0088cc]/15 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">{claim.claimNumber}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              PO {claim.poNumber}{claim.rfqNumber ? ` · RFQ ${claim.rfqNumber}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-[#0088cc]/10">
            <MdClose className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <span className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border', status.className)}>
            {status.icon}
            {status.label}
          </span>

          <div className="grid grid-cols-2 gap-4 text-sm rounded-xl bg-[#0088cc]/5 border border-[#0088cc]/15 p-4">
            <div><p className="text-[#0088cc]/65 text-xs font-bold uppercase">Customer</p><p className="font-semibold">{claim.customerName || '—'}</p></div>
            <div><p className="text-[#0088cc]/65 text-xs font-bold uppercase">Vessel</p><p className="font-semibold">{claim.vesselName || '—'}</p></div>
            <div><p className="text-[#0088cc]/65 text-xs font-bold uppercase">Category</p><p className="font-semibold">{claim.category || '—'}</p></div>
            <div><p className="text-[#0088cc]/65 text-xs font-bold uppercase">Brand / Model</p><p className="font-semibold">{[claim.brand, claim.equipmentModel].filter(Boolean).join(' / ') || '—'}</p></div>
          </div>

          <div>
            <p className="text-[#0088cc]/65 text-xs font-bold uppercase mb-1">Customer reason</p>
            <p className="text-sm text-[hsl(var(--foreground))] whitespace-pre-wrap rounded-xl border border-[#0088cc]/15 p-4">{claim.reason}</p>
          </div>

          <div>
            <p className="text-[#0088cc]/65 text-xs font-bold uppercase mb-2">Claimed Items ({claim.items.length})</p>
            <div className="overflow-x-auto rounded-xl border border-[#0088cc]/15">
              <table className="w-full text-sm">
                <thead className="bg-[#0088cc]/5">
                  <tr>
                    {['Description', 'Part No', 'Qty', 'UOM', 'Unit Price', 'Total'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {claim.items.map((item, idx) => (
                    <tr key={idx} className="border-t border-[#0088cc]/10">
                      <td className="px-3 py-2 font-medium text-[hsl(var(--foreground))]">{item.itemDescription}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{item.partNo || '—'}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{item.quantity}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{item.uom}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{item.unitPrice ?? '—'}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{item.total ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {claim.documents && claim.documents.length > 0 && (
            <div>
              <p className="text-[#0088cc]/65 text-xs font-bold uppercase mb-2">Customer attachments</p>
              <div className="flex flex-wrap gap-3">
                {claim.documents.map((doc, idx) => (
                  <FileDownloadLink
                    key={idx}
                    href={resolveFileUrl(doc.fileUrl)}
                    fileName={doc.fileName}
                    className="inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--primary))] hover:underline"
                    onError={(message) => showToast(message, 'error')}
                  >
                    <MdVisibility className="w-3.5 h-3.5" />
                    {doc.fileName}
                  </FileDownloadLink>
                ))}
              </div>
            </div>
          )}

          {claim.status === 'pending' && (
            <div className="border-t border-[#0088cc]/15 pt-5 space-y-4">
              {showRejectForm && (
                <div>
                  <label className="block text-xs font-bold uppercase text-[hsl(var(--muted-foreground))] mb-2">Rejection reason *</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    placeholder="Explain why this claim is being rejected…"
                    className="w-full rounded-xl border border-[#0088cc]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  />
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={respondMutation.isPending}
                  onClick={() => respondMutation.mutate('accept')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
                >
                  <MdCheckCircle className="w-4 h-4" />
                  Accept Claim
                </button>
                {!showRejectForm ? (
                  <button
                    type="button"
                    onClick={() => setShowRejectForm(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-red-200 text-red-700 text-sm font-bold hover:bg-red-50"
                  >
                    <MdCancel className="w-4 h-4" />
                    Reject Claim
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!rejectionReason.trim() || respondMutation.isPending}
                    onClick={() => respondMutation.mutate('reject')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
                  >
                    Confirm Rejection
                  </button>
                )}
              </div>
            </div>
          )}

          {claim.status === 'rejected' && claim.rejectionReason && (
            <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-red-800">
              <p className="font-bold mb-1">Your rejection reason</p>
              <p>{claim.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ClaimRequestsPage({
  mode = 'received',
  embedded = false,
}: {
  mode?: 'received' | 'created';
  embedded?: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<ClaimRecord | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const isReceived = mode === 'received';

  const { data: claims = [], isLoading } = useQuery<ClaimRecord[]>({
    queryKey: ['admin-delivery-claims', mode],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/v1/admin/claims?direction=${mode}`);
      if (!res.ok) throw new Error('Failed to load claim requests');
      const json = await res.json();
      return json.data || [];
    },
  });

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter((claim) =>
      [claim.claimNumber, claim.poNumber, claim.rfqNumber, claim.customerName, claim.vesselName, claim.category, claim.brand]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [claims, searchQuery]);

  return (
    <div
      className={cn(
        'w-full font-sans text-[hsl(var(--foreground))]',
        embedded ? '' : 'min-h-screen bg-[hsl(var(--background))] p-4 lg:p-8'
      )}
    >
      {!embedded && (
        <PageHeader
          title={isReceived ? 'Claims Received' : 'Claims Created'}
          subtitle={
            isReceived
              ? 'Review delivery claims raised by customers for damaged or defective items after receipt.'
              : 'Claims you raised against vendor purchase orders.'
          }
          subtitleVariant="description"
          subtitleAccent={false}
          breadcrumb={[
            { label: 'Admin' },
            { label: isReceived ? 'Claims Received' : 'Claims Created', current: true },
          ]}
          actions={
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0088cc] text-white text-sm font-bold hover:bg-[#0077b3]"
            >
              <MdAdd className="w-5 h-5" />
              Create New Claim
            </button>
          }
        />
      )}

      {embedded && !isReceived && (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0088cc] text-white text-sm font-bold hover:bg-[#0077b3]"
          >
            <MdAdd className="w-5 h-5" />
            Create New Claim
          </button>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by claim #, PO, RFQ, customer, vessel…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-xl rounded-xl border border-[#0088cc]/20 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
        />
        <ClearFiltersButton
          visible={!!searchQuery.trim()}
          onClick={() => setSearchQuery('')}
          variant="toolbar"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[#0088cc]/15 shadow-sm overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0088cc]/5 border-b border-[#0088cc]/15">
              <tr>
                {['Date', 'Claim #', 'PO #', 'RFQ #', 'Vessel', 'Customer', 'Items', 'Status', 'Action'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableBodySkeleton rows={6} cols={9} />
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <MdReportProblem className="w-10 h-10 text-[#0088cc]/45 mx-auto mb-2" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">No claim requests found.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((claim) => {
                  const status = statusConfig[claim.status];
                  return (
                    <tr key={claim._id} className="border-b border-[#0088cc]/10 hover:bg-[#0088cc]/5">
                      <td className="px-4 py-4 text-sm text-[hsl(var(--muted-foreground))]">
                        <div>{formatDate(claim.createdAt)}</div>
                        <div className="text-xs text-[#0088cc]/65">{formatTime(claim.createdAt)}</div>
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-[hsl(var(--foreground))]">{claim.claimNumber}</td>
                      <td className="px-4 py-4 text-sm text-[hsl(var(--foreground))]">{claim.poNumber}</td>
                      <td className="px-4 py-4 text-sm text-[hsl(var(--foreground))]">{claim.rfqNumber || '—'}</td>
                      <td className="px-4 py-4 text-sm text-[hsl(var(--foreground))]">{claim.vesselName || '—'}</td>
                      <td className="px-4 py-4 text-sm text-[hsl(var(--foreground))]">{claim.customerName || '—'}</td>
                      <td className="px-4 py-4 text-sm text-[hsl(var(--foreground))]">{claim.items.length}</td>
                      <td className="px-4 py-4">
                        <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border', status.className)}>
                          {status.icon}
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => setSelectedClaim(claim)}
                          className="inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--primary))] hover:underline"
                        >
                          <MdVisibility className="w-4 h-4" />
                          {claim.status === 'pending' ? 'Review' : 'View'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedClaim && (
        <ClaimDetailModal claim={selectedClaim} onClose={() => setSelectedClaim(null)} />
      )}
      {showCreate && <RaiseClaimModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
